// src/features/people/components/AssignTrainingModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, InputGroup, Spinner, Alert } from 'react-bootstrap';
import { FaSearch, FaPlus } from 'react-icons/fa';
import dayjs from 'dayjs';

import PeopleService from '../PeopleService';
import { getTipoDescripcion, TIPOS_CAPACITACIONES, FORMATO_FECHA } from '../../../utils/constants';

const AssignTrainingModal = ({ show, onHide, personId, currentTrainings = [], onTrainingAssigned }) => {
  const [trainings, setTrainings] = useState([]);
  const [filteredTrainings, setFilteredTrainings] = useState([]);
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState('');

  // Función para normalizar texto removiendo acentos/tildes
  const normalizeText = (text) => {
    return text
      .normalize('NFD') // Descompone caracteres con acentos
      .replace(/[\u0300-\u036f]/g, '') // Remueve marcas diacríticas (acentos)
      .toLowerCase();
  };

  useEffect(() => {
    if (show && personId) {
      loadAvailableTrainings();
    }
  }, [show, personId]);

  useEffect(() => {
    filterTrainings();
  }, [searchTerm, trainings]);

  // Limpiar selección cuando se cierra el modal
  useEffect(() => {
    if (!show) {
      setSelectedTraining(null);
      setSearchTerm('');
      setError('');
    }
  }, [show]);

  const loadAvailableTrainings = async () => {
    try {
      setLoading(true);
      const available = await PeopleService.getAvailableTrainings(personId);
      const assignedIds = currentTrainings.map(t => t.id);
      const unassigned = available.filter(t => !assignedIds.includes(t.id));

      setTrainings(unassigned);
      setFilteredTrainings(unassigned);
    } catch (err) {
      console.error('Error al cargar capacitaciones disponibles:', err);
      setError('Error al cargar la lista de capacitaciones disponibles');
    } finally {
      setLoading(false);
    }
  };

  const filterTrainings = () => {
    if (!searchTerm.trim()) {
      setFilteredTrainings(trainings);
      return;
    }

    const normalizedSearchTerm = normalizeText(searchTerm);
    const filtered = trainings.filter(t => {
      const tipoDesc = getTipoDescripcion(t.type, TIPOS_CAPACITACIONES) || '';
      const topic = t.topic || '';
      const fullDesc = `${tipoDesc} ${topic}`;
      const normalizedDesc = normalizeText(fullDesc);
      
      return normalizedDesc.includes(normalizedSearchTerm);
    });

    setFilteredTrainings(filtered);
  };

  const handleSelectTraining = (training) => {
    // Si ya está seleccionada, la deseleccionamos
    if (selectedTraining && selectedTraining.id === training.id) {
      setSelectedTraining(null);
    } else {
      // Si no está seleccionada, la seleccionamos
      setSelectedTraining(training);
    }
    setError('');
  };

  const handleAssignTraining = async () => {
    if (!selectedTraining) {
      setError('Por favor seleccione una capacitación');
      return;
    }

    try {
      setAssigning(true);
      setError('');
      const result = await PeopleService.assignPersonToTraining(personId, selectedTraining.id);

      if (result.success === false) throw new Error(result.error || 'Error al asignar capacitación');

      if (onTrainingAssigned) onTrainingAssigned();
      onHide();
    } catch (err) {
      console.error('Error al asignar capacitación:', err);
      setError(`Error al asignar la capacitación: ${err.message || 'Error desconocido'}`);
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} backdrop="static" keyboard={false} centered>
      <Modal.Header closeButton >
        <Modal.Title className='text-center w-100'>Asignar Capacitación</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <p className="text-muted mb-3">Seleccione la capacitación que desea asignar a esta persona.</p>

        <InputGroup className="mb-3">
          <Form.Control
            placeholder="Buscar por tipo o tema..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={loading}
          />
          <InputGroup.Text>
            <FaSearch />
          </InputGroup.Text>
        </InputGroup>

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <p className="mt-2">Cargando capacitaciones...</p>
          </div>
        ) : (
          <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '0.25rem' }}>
            {filteredTrainings.length === 0 ? (
              <div className="text-center py-4 text-muted">
                No hay capacitaciones disponibles{searchTerm ? ' que coincidan con la búsqueda' : ''}
              </div>
            ) : (
              <ul className="list-group list-group-flush">
                {filteredTrainings.map(training => (
                  <li
                    key={training.id}
                    className={`list-group-item list-group-item-action ${
                      selectedTraining && selectedTraining.id === training.id ? 'active' : ''
                    }`}
                    onClick={() => handleSelectTraining(training)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="fw-bold">
                      {getTipoDescripcion(training.type, TIPOS_CAPACITACIONES)}
                    </div>
                    <div className="small text-muted">
                      {training.topic || 'Sin tema'} - Fecha: {dayjs(training.date).format(FORMATO_FECHA)}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Mostrar información de la capacitación seleccionada */}
        {selectedTraining && (
          <div className="mt-3 p-3 bg-light rounded">
            <h6 className="mb-2">Capacitación seleccionada:</h6>
            <div><strong>Tipo:</strong> {getTipoDescripcion(selectedTraining.type, TIPOS_CAPACITACIONES)}</div>
            <div><strong>Tema:</strong> {selectedTraining.topic || 'Sin tema'}</div>
            <div><strong>Fecha:</strong> {dayjs(selectedTraining.date).format(FORMATO_FECHA)}</div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={assigning}>Cancelar</Button>
        <Button variant="primary" onClick={handleAssignTraining} disabled={!selectedTraining || assigning}>
          {assigning ? (
            <>
              <Spinner as="span" animation="border" size="sm" className="me-2" />
              Asignando...
            </>
          ) : (
            <>
              <FaPlus className="me-1" /> Asignar Capacitación
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AssignTrainingModal;